import { ArrowForward } from '@mui/icons-material';
import {
    Box,
    Divider,
    Fab,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import GuardedLink from '../../../../common/auth/GuardedLink';
import { EntityTypes, ResourceType } from '../../../../common/PricePrgClient';
import { RootState } from '../../../../store';
import { includes, startsWith } from '../../../../utils';
import { customerSelected } from '../../../customer/redux/customerSlices';
import { getSelectedPricelistCustomers, getSelectedPricelists } from '../../redux/pricelistSelectors';
import { fetchCustomersForPricelist, showConnectPricelistDialog } from '../../redux/pricelistSlices';

const mapStateToProps = (state: RootState) => {
    return {
        selectedPricelist: getSelectedPricelists(state)[0],
        customers: getSelectedPricelistCustomers(state),
        appRestrictions: state.settings.applicationRestrictions
    };
};
const customerFilterSubject = new BehaviorSubject<string>('');

type Props = ReturnType<typeof mapStateToProps>;

const CustomersPricelistTab = ({ selectedPricelist, customers, appRestrictions }: Props) => {
    const dispatch = useDispatch();
    const [filteredCustomers, setFilteredCustomers] = useState(customers);
    const orderedCustomers = [...filteredCustomers].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
    const [customerFilter, setCustomerFilter] = useState('');

    const handleOnFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        customerFilterSubject.next(event.target.value);
        setCustomerFilter(event.target.value);
    };

    const filterCustomers = useCallback(
        (filter: string) => {
            if (filter.length < 3) {
                return customers;
            }
            return customers.filter(
                cust =>
                    includes(filter.trim(), cust.name) ||
                    startsWith(filter.trim(), cust.customerNumber?.toString())
            );
        },
        [customers]
    );

    useEffect(() => {
        if (selectedPricelist != null) {
            dispatch(fetchCustomersForPricelist.request(selectedPricelist.pricelistId));
        }
    }, [dispatch, selectedPricelist]);

    useEffect(() => {
        setFilteredCustomers(filterCustomers(''));
    }, [filterCustomers]);

    useEffect(() => {
        const subscription = customerFilterSubject
            .pipe(
                debounceTime(200),
                tap(cf => {
                    setFilteredCustomers(filterCustomers(cf));
                })
            )
            .subscribe();
        return () => subscription.unsubscribe();
    }, [filterCustomers, customers, setCustomerFilter]);

    if (selectedPricelist === null || selectedPricelist === undefined) {
        return (
            <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 400, marginTop: '5%' }}>
                Choose a pricelist to view more details about it.
            </Typography>
        );
    }

    const dispatchGotoSelectedCustomer = (customerId: number) => {
        dispatch(customerSelected(customerId));
    };

    const PricelistDivider = () => (
        <Grid container direction="row">
            <Grid item xs={12} md={12} lg={6}>
                <Divider sx={{ margin: '16px 0' }} orientation="horizontal" variant="fullWidth" />
            </Grid>
        </Grid>
    );
    const handleConnectPricelist = () => {
        dispatch(
            showConnectPricelistDialog({ pricelistId: selectedPricelist.pricelistId, customerId: null })
        );
    };
    return (
        <Box component="div" sx={{ width: '60rem' }}>
            <Box sx={{ display: 'inline-flex' }}>
                <Typography variant="h6">
                    Customers linked to pricelist: &quot;{selectedPricelist.name}&quot;
                </Typography>
                <Tooltip title="Connect pricelist">
                    <Fab
                        sx={{ ml: 2 }}
                        color="primary"
                        disabled={appRestrictions[EntityTypes.PricelistConnection].isCreateDisabled}
                        size="small"
                        onClick={handleConnectPricelist}
                    >
                        <LinkIcon />
                    </Fab>
                </Tooltip>
            </Box>
            <PricelistDivider />
            <Grid container direction="column" spacing={2}>
                <TextField
                    sx={{ display: 'block', margin: '16px 0' }}
                    label=""
                    variant="standard"
                    onChange={handleOnFilterChange}
                    value={customerFilter}
                    placeholder="Filter"
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Cust.No.</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderedCustomers.map(cust => (
                            <TableRow key={cust.customerId}>
                                <TableCell>{cust.customerNumber}</TableCell>
                                <TableCell>{cust.name}</TableCell>
                                <TableCell>
                                    <GuardedLink
                                        resource={ResourceType.CustomerPage}
                                        link={'/customers'}
                                        childComponents={
                                            <Tooltip sx={{ display: 'inline-flex' }} title="Go to customer">
                                                <IconButton
                                                    onClick={() =>
                                                        dispatchGotoSelectedCustomer(cust.customerId!)
                                                    }
                                                    size="small"
                                                >
                                                    <ArrowForward />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>
        </Box>
    );
};

export default connect(mapStateToProps)(CustomersPricelistTab);
